import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Card from "../components/Card";
import Videos from "../components/Videos";
import Clients from "../components/Clients";
import AdvertisingHeader from "../components/AdvertisingHeader";
import SEO from "../components/SEO";
import {
  ClientSection,
  FAQSection,
  ReachOutSection,
} from "../components/BottomSection";
import axios from "../lib/axios";
import { useLocation } from "react-router-dom";
import { FaArrowDown } from "react-icons/fa";
import CelebrityShoutouts from "../components/CelebrityShoutouts";

const designFaqs = [
  {
    question: "What is influencer marketing?",
    answer:
      "Influencer marketing is a strategy where brands collaborate with individuals who have a significant online following (influencers) to promote products or services. These influencers help reach a targeted audience, building trust and boosting brand awareness.",
  },
  {
    question: "Why is influencer marketing important for my business?",
    answer:
      "Influencer marketing allows your brand to connect authentically with a specific audience. Influencers have a trusted relationship with their followers, which can lead to higher engagement, credibility, and conversions for your brand.",
  },
  {
    question: "Can you help us choose the right influencers for our campaign?",
    answer:
      "Yes, we carefully analyze your brand, goals, and target audience to identify influencers who align with your brand values and can deliver maximum impact.",
  },
];

const influencerReachOut = {
  title: "Become an Influencer Partner with Indorinama",
  description:
    "Collaborate with us to promote brands and products to your audience.",
  formFields: [
    { name: "name", label: "Your Name", type: "text", required: true },
    { name: "mobile", label: "Mobile No.", type: "tel", required: true },
    { name: "email", label: "Email ID", type: "email", required: true },
    { name: "city", label: "City", type: "text", required: true },
    {
      name: "influencerType",
      label: "Influencer Type",
      type: "select",
      options: [
        "Lifestyle",
        "Fashion",
        "Beauty",
        "Food",
        "Travel",
        "Gaming",
        "Technology",
      ],
      required: true,
    },
    {
      name: "influencerInstagram",
      label: "Instagram URL",
      type: "url",
      required: true,
    },
  ],
};

const InfluencerMarketing = () => {
  const location = useLocation();
  const { highlightCard, scrollToCard } = location.state || {};
  const [categories, setCategories] = useState([]);

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          "https://admin.indorinama.in/api/categories",
        );
        // Filter categories for influencer marketing only
        const influencerCategories = response.data.docs.filter(
          (cat) => cat.for === "influencer",
        );
        setCategories(influencerCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (scrollToCard && highlightCard) {
      console.log({ scrollToCard, highlightCard });
      const element = document.getElementById(`design-${highlightCard}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        element.classList.add("highlight-card");
        // Remove highlight after 2 seconds
        setTimeout(() => {
          element.classList.remove("highlight-card");
        }, 2000);
      }
    }
  }, [highlightCard, scrollToCard, location]);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("popularity");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    category: [],
    platform: [],
    priceRange: [],
    tier: [],
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 12,
  });
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      setError(null);

      try {
        // Determine sort parameter based on sortBy value
        let sortParam = "-createdAt";
        switch (sortBy) {
          case "price_low":
            sortParam = "+price";
            break;
          case "price_high":
            sortParam = "-price";
            break;
          case "name":
            sortParam = "name";
            break;
          case "popularity":
          default:
            sortParam = "-createdAt";
            break;
        }

        // Build query params from filters
        const filterParams = new URLSearchParams();
        filterParams.append("page", pagination.page);
        filterParams.append("limit", pagination.limit);
        filterParams.append("sort", sortParam);

        // Add category filters - use category IDs from the API
        if (selectedFilters.category?.length > 0) {
          const categoryIds = selectedFilters.category
            .map((catName) => categories.find((c) => c.name === catName)?.id)
            .filter(Boolean);
          if (categoryIds.length > 0) {
            // Based on the prompt, the API expects multiple 'category' params
            // with individual values, not one comma-separated 'categories' param.
            // Assuming categoryIds is the correct list of values to send.
            categoryIds.forEach((categoryId) => {
              filterParams.append("category", categoryId);
            });
          }
        }

        // Add platform filters
        if (selectedFilters.platform?.length > 0) {
          // filterParams.append('platforms', selectedFilters.platform.join(','));
          selectedFilters.platform.forEach((platform) => {
            filterParams.append("platform", platform);
          });
        }

        // Add price range filter
        if (selectedFilters.priceRange?.length > 0) {
          const priceRangeMap = {
            under5K: { min: 0, max: 5000 },
            "5K-10K": { min: 5000, max: 10000 },
            "10K-25K": { min: 10000, max: 25000 },
            "25K-50K": { min: 25000, max: 50000 },
            above50K: { min: 50000, max: null },
          };
          const selectedRange = priceRangeMap[selectedFilters.priceRange[0]];

          if (selectedRange) {
            filterParams.append("minimum_billing_gte", selectedRange.min);
            if (selectedRange.max) {
              filterParams.append("minimum_billing_lte", selectedRange.max);
            }
          }
        }

        // Add tier filter
        if (selectedFilters.tier?.length > 0) {
          selectedFilters.tier.forEach((tier) => {
            filterParams.append("tier", tier);
          });
        }

        const url = `/api/influencer-ads/search?${filterParams.toString()}`;
        const response = await axios.get(url);
        const data = response.data;

        if (response.status === 200) {
          const results = Array.isArray(data.docs) ? data.docs : [];

          if (pagination.page === 1) {
            setDesigns(results);
          } else {
            setDesigns((prev) => [...prev, ...results]);
          }

          if (data) {
            setPagination((prev) => ({
              ...prev,
              total: data.totalDocs,
              pages: data.totalPages,
            }));
            setHasMore(pagination.page < data.totalPages);
          }
        } else {
          setError(data.message || "Failed to fetch designs");
        }
      } catch (error) {
        setError("Failed to fetch design services");
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [pagination.page, pagination.limit, selectedFilters, sortBy, categories]);

  const filteredAndSortedDesigns = useMemo(() => {
    if (!designs || !Array.isArray(designs)) return [];

    return designs.filter((item) => {
      if (!item) return false;

      return Object.entries(selectedFilters).every(
        ([filterType, filterValues]) => {
          if (!filterValues || filterValues.length === 0) return true;

          switch (filterType) {
            case "category":
              return filterValues.includes(item.category);
            case "platform":
              return filterValues.some(
                (platform) =>
                  item.platforms && item.platforms.includes(platform),
              );
            default:
              return true;
          }
        },
      );
    });
  }, [designs, selectedFilters]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setDesigns([]);
    setHasMore(true);
  }, [selectedFilters]);

  const sortOptions = [
    { value: "popularity", label: "Sort by Popularity" },
    { value: "name", label: "Sort by Name" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
  ];

  const handleSortClick = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
  };

  const handleFilterChange = (filterType, values) => {
    let newFilters;
    if (filterType === "priceRange") {
      if (selectedFilters.priceRange?.includes(values)) {
        // Deselect if already selected
        newFilters = {
          ...selectedFilters,
          priceRange: [],
        };
      } else {
        // Select new price range
        newFilters = {
          ...selectedFilters,
          priceRange: [values],
        };
      }
    } else {
      // Handle other filter types (category, platform, tier)
      newFilters = {
        ...selectedFilters,
        [filterType]: values,
      };
    }

    setSelectedFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setDesigns([]); // Clear existing designs when filters change
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Influencer Marketing"
        description="Connect with top influencers and celebrities for your brand promotion. Find influencers across Instagram, YouTube, and more at Indorinama."
        keywords="influencer marketing, celebrity endorsement, brand collaboration, social media influencers, influencer advertising"
        url="https://indorinama.com/influencer-marketing"
      />
    <div className="influencer-marketing">
      <Navbar />
      <CelebrityShoutouts />
      <div className="digital-section">
        <div className="digital">
          <div className="md:max-w-[80vw] mx-auto">
            <AdvertisingHeader
              category="influencer-marketing"
              sortOptions={sortOptions}
              showFilterOptions={true}
              sortBy={sortBy}
              handleSortClick={handleSortClick}
              showSortDropdown={showSortDropdown}
              setShowSortDropdown={setShowSortDropdown}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              filterOptions={{
                category: categories.map((cat) => cat.name),
                platform: [
                  "Instagram",
                  "LinkedIn",
                  "Facebook",
                  "YouTube",
                  "Twitter",
                ],
                tier: ["Tier 1", "Tier 2", "Tier 3", "Tier 4", "Tier 5"],
              }}
            />
            <p className="text-gray-600 px-4 -mt-6">
              Showing {filteredAndSortedDesigns.length} of {pagination.total}{" "}
              results
            </p>
          </div>

          <section className="py-8 mb-6">
            <div className="md:max-w-[80vw] mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-center mb-4">
                <img src="/title.svg" alt="title" className="w-full h-16" />
              </div>
              <div className="flex justify-center">
                <div className="flex flex-wrap gap-6 justify-center">
                  <button
                    onClick={() => handleFilterChange("priceRange", "under5K")}
                    className="relative hover:scale-105 transition-all duration-300 bg-[#DC2626] z-[49] h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
                  >
                    <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                      <span className="-ml-2 -mt-2">Under ₹5K</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleFilterChange("priceRange", "5K-10K")}
                    className="relative hover:scale-105 transition-all duration-300 bg-[#B31F1F] z-20 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
                  >
                    <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                      <span className="-ml-2 -mt-2">₹5K - ₹10K</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleFilterChange("priceRange", "10K-25K")}
                    className="relative hover:scale-105 transition-all duration-300 bg-[#8D1818] z-10 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
                  >
                    <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                      <span className="-ml-2 -mt-2">₹10K - ₹25K</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleFilterChange("priceRange", "25K-50K")}
                    className="relative hover:scale-105 transition-all duration-300 bg-[#681212] z-0 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
                  >
                    <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                      <span className="-ml-2 -mt-2">₹25K - ₹50K</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleFilterChange("priceRange", "above50K")}
                    className="relative hover:scale-105 transition-all duration-300 bg-[#440F0F] z-0 h-20 w-[200px] text-white font-semibold text-2xl group
                before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white before:rounded-full before:top-0 before:left-0 before:-translate-x-1/2 before:-translate-y-1/2
                after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0 after:right-0 after:translate-x-1/2 after:-translate-y-1/2
                [&>div::before]:content-[''] [&>div::before]:absolute [&>div::before]:w-5 [&>div::before]:h-5 [&>div::before]:bg-white [&>div::before]:rounded-full [&>div::before]:bottom-0 [&>div::before]:left-[-7px] [&>div::before]:-translate-x-1/2 [&>div::before]:translate-y-1/4
                [&>div::after]:content-[''] [&>div::after]:absolute [&>div::after]:w-5 [&>div::after]:h-5 [&>div::after]:bg-white [&>div::after]:rounded-full [&>div::after]:bottom-0 [&>div::after]:right-2 [&>div::after]:translate-x-1/2 [&>div::after]:translate-y-1/3"
                  >
                    <div className="relative z-10 flex items-center justify-center w-full h-full border-2 border-dashed border-white/30 m-2">
                      <span className="-ml-2 -mt-2">Above ₹50K</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <section className="py-4">
        <div className="md:max-w-[80vw] mx-auto px-4 sm:px-6 lg:px-8">
          {designs.length === 0 && !loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No designs found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredAndSortedDesigns.map((design, index) => {
                if (filteredAndSortedDesigns.length === index + 1) {
                  return (
                    <div key={design._id}>
                      <Card
                        data={design}
                        category="influencer-marketing"
                        options={true}
                      />
                    </div>
                  );
                } else {
                  return (
                    <Card
                      key={design._id}
                      data={design}
                      category="influencer-marketing"
                      options={true}
                    />
                  );
                }
              })}
            </div>
          )}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                className="flex items-center px-6 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
              >
                Load More
                <FaArrowDown className="ml-2" />
              </button>
            </div>
          )}
          {loading && (
            <div className="flex justify-center mt-8">
              <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </section>

      {/* <div className="flex justify-center items-center">
        <img src={img1} alt="achievements" />
      </div> */}
      {/* Floating Stats */}
      <div className="mx-auto max-w-[80vw] grid gap-4 my-10">
        <img alt="" src="/influncer.svg" className="w-full h-auto" />
      </div>
      <Clients category="influencer" />
      <Videos category="influencer-marketing" />
      <FAQSection pageFAQ={designFaqs} />
      <div className="-mt-16 -mb-6">
        <ClientSection key="influencer_client_logos" />
      </div>
      <ReachOutSection
        title={influencerReachOut.title}
        description={influencerReachOut.description}
        formFields={influencerReachOut.formFields}
        category="influencer-marketing"
      />
      <Footer />
    </div>
    </>
  );
};

export default InfluencerMarketing;
